import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const SHORT_ACTIVATE_DELAY = 0;
const LONG_ACTIVATE_DELAY = 250;
const DEACTIVATE_DELAY = 350;
const SUBMENU_EXTRA_SPACE_PX = 30; // prevent caption clipping

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  /** @type {ResizeObserver | null} */
  #submenuResizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

  // onDocumentReady(this.#preloadImages); // Disabled: was force-loading all lazy menu images on page load, causing memory pressure on iOS/Safari

    // Close button inside submenu (desktop mega menu)
    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {Element} */ (event.target);
        if (!(target instanceof Element)) return;
        const closeBtn = target.closest('.menu-list__submenu-close');
        if (!closeBtn) return;
        event.preventDefault();
        event.stopPropagation();
        this.#debouncedDeactivate.cancel();
        this.#forceClose();
      },
      { signal: this.#abortController.signal }
    );

    // Close on Escape when menu is open
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        if (this.getAttribute('aria-expanded') !== 'true') return;
        event.preventDefault();
        this.#debouncedDeactivate.cancel();
        this.#forceClose();
      },
      { signal: this.#abortController.signal }
    );

    // Close when clicking outside the header menu / mega menu
    document.addEventListener(
      'click',
      (event) => {
        const target = /** @type {Element} */ (event.target);
        if (!(target instanceof Element)) return;
        const insideMenu = !!target.closest('header-menu');
        if (insideMenu) return;
        // Deactivate any open submenu
        this.#debouncedDeactivate.cancel();
        this.#deactivate();
      },
      { signal: this.#abortController.signal }
    );

    // Click-to-open: first click opens submenu (no navigation), second click navigates
    this.addEventListener(
      'click',
      (event) => {
        const target = /** @type {Element} */ (event.target);
        if (!(target instanceof Element)) return;

        const item = /** @type {HTMLElement | null} */ (target.closest('a[role="menuitem"]'));
        if (!item) return;

        const submenu = findSubmenu(item);
        if (!submenu) return; // no submenu -> allow normal navigation

        event.preventDefault();

        // If already open, navigate
        if (item.getAttribute('aria-expanded') === 'true') {
          const href = item.getAttribute('href');
          if (href) window.location.href = href;
          return;
        }

        // Open submenu
        this.#debouncedDeactivate.cancel();
        this.#shortDebouncedActivateHandler.cancel();
        this.#longDebouncedActivateHandler.cancel();

        this.dispatchEvent(new MegaMenuHoverEvent());
        this.removeAttribute('data-animating');

        const previouslyActiveItem = this.#state.activeItem;
        if (previouslyActiveItem) {
          previouslyActiveItem.ariaExpanded = 'false';
        }

        this.#state.activeItem = item;
        this.ariaExpanded = 'true';
        item.ariaExpanded = 'true';

        const updateHeight = () => {
          const overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;
          const candidate = submenu.querySelector('.menu-list__submenu-inner');
          const inner = candidate instanceof HTMLElement ? candidate : submenu;
          const contentHeight = inner.scrollHeight;
          const submenuHeight = Math.max(contentHeight, overflowMenuHeight) + SUBMENU_EXTRA_SPACE_PX;
          this.style.setProperty('--submenu-height', `${submenuHeight}px`);
        };
        updateHeight();

        // Observe dynamic height changes (e.g., alt text length changes on hover)
        this.#submenuResizeObserver?.disconnect();
        this.#submenuResizeObserver = new ResizeObserver(updateHeight);
        const candidate = submenu.querySelector('.menu-list__submenu-inner');
        const innerObs = candidate instanceof HTMLElement ? candidate : submenu;
        if (innerObs) this.#submenuResizeObserver.observe(innerObs);
      },
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (this.#state.activeItem) {
      this.#shortDebouncedActivateHandler(event);
    } else {
      this.#longDebouncedActivateHandler(event);
    }
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const inner = /** @type {HTMLElement | null} */ (submenu?.querySelector('.menu-list__submenu-inner')) || submenu;

    const updateHeight = () => {
      const currentOverflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;
      const contentHeight = inner ? inner.scrollHeight : (submenu?.scrollHeight ?? 0);
      const submenuHeight = submenu ? Math.max(contentHeight, currentOverflowMenuHeight) + SUBMENU_EXTRA_SPACE_PX : 0;
      this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    };
    updateHeight();

    // If submenu content changes height after hover (lazy images, font rendering, etc),
    // keep the clip-path height in sync so the bottom isn't clipped.
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = new ResizeObserver(updateHeight);
    if (inner instanceof HTMLElement) this.#submenuResizeObserver.observe(inner);
  };

  #shortDebouncedActivateHandler = debounce(this.#activateHandler, SHORT_ACTIVATE_DELAY);
  #longDebouncedActivateHandler = debounce(this.#activateHandler, LONG_ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#shortDebouncedActivateHandler.cancel();
    this.#longDebouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.#closeItem(item);
  };

  /**
   * Force-close the active item even if hovered.
   * Used by explicit close controls (X button, Escape).
   */
  #forceClose = () => {
    const item = this.#state.activeItem;
    if (!item) return;
    this.#closeItem(item);
  };

  /**
   * Close a menu item and reset state.
   * @param {HTMLElement} item
   */
  #closeItem = (item) => {
    this.style.setProperty('--submenu-height', '0px');
    this.dataset.overflowExpanded = 'false';

    // Stop observing submenu height changes
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, this.animationDelay);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[role="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
